import React, { useEffect, useState } from 'react';
import postal from 'postal';
import { getAmplitudeInstance } from '@alltrails/shared/utils/amplitudeHelpers';
import { string, object, bool, shape } from 'prop-types';
import Nav from 'react-bootstrap/Nav';
import Col from 'react-bootstrap/Col';
import Navbar from 'react-bootstrap/Navbar';
import NavDropdown from 'react-bootstrap/NavDropdown';
import Dropdown from 'react-bootstrap/Dropdown';
import classNames from 'classnames';
import { GLOBAL_NAV_SIGNUP_BUTTON_TAPPED, PRO_GLOBAL_HEADER_UPGRADE_CLICKED } from 'utils/constants/AmplitudeEvent';
import { StringConstantsMixin } from 'react_components/mixins/shared/string_constants_mixin';
import { generateNavigationProps } from 'utils/HeaderHelper';
import { checkLinkAndAppendLanguage } from 'utils/localization';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import useIpCountryCode from 'hooks/useIpCountryCode';
import { getShopLink } from 'utils/Links';
import { debounce } from 'debounce';
import * as styles from './styles/styles.module.scss';
import MainButton from '../shared/MainButton';
import ProfileIcon from '../shared/ProfileIcon';
import Logo from '../shared/Logo';

require('./styles/dropdown.scss');
require('./styles/nav.css');
require('./styles/position.css');

const {
  NAVIGATION: { LISTS, COMPLETED },
  UPGRADE_TO_PRO
} = StringConstantsMixin.strings;

function renderDropdownItems(dropdowns) {
  if (!dropdowns || !dropdowns.length) {
    return null;
  }

  return dropdowns.map(item => (
    <div key={`dropdown-${item.label}`}>
      <Dropdown.Item
        className={`${styles.item} ${item.underline ? styles.underline : ''}`}
        href={item.linkPath}
        data-testid={item.label}
        key={item.label}
        onClick={item.onClick}
        {...(item.openInNewWindow ? { target: '_blank' } : {})}
      >
        {item.label}
      </Dropdown.Item>
      {item.label === LISTS || item.label === COMPLETED ? <Dropdown.Divider /> : ''}
    </div>
  ));
}

function Navigation({ currentUser, featureFlags, messagingChannel, proUser }) {
  const ipCountryCode = useIpCountryCode();
  const languageRegionCode = useLanguageRegionCode();
  const [activeDropdown, setActiveDropdown] = useState(false);

  // props passed in for state for logging in and out without a page refresh
  const [navigationData, setNavigationData] = useState(
    generateNavigationProps({
      currentUser,
      featureFlags,
      languageRegionCode,
      proUser
    })
  );

  const { user, links, brand } = navigationData;

  useEffect(() => {
    if (messagingChannel) {
      messagingChannel.subscribe('navigation.loginUser', data => {
        setNavigationData(generateNavigationProps(data));
      });
    }

    return () => {
      if (messagingChannel && typeof messagingChannel.unsubscribe === 'function') {
        messagingChannel.unsubscribe('navigation.loginUser');
      }
    };
  }, [messagingChannel]);

  // Debounce open and close handling to avoid close/open flashes when mousing between the dropdown toggle and the dropdown menu
  const handleDropdownOpenAndClose = debounce(dropdownId => {
    setActiveDropdown(dropdownId);
  }, 200);

  function renderLinkNode(linkNode, className) {
    if (linkNode.dropdown) {
      return (
        <Dropdown
          show={activeDropdown === linkNode.label || false}
          as={Nav.Item}
          className={classNames(styles.dropdown, className)}
          data-testid={linkNode.label}
          onMouseEnter={() => handleDropdownOpenAndClose(linkNode.label)}
          onMouseLeave={() => handleDropdownOpenAndClose('')}
        >
          <Dropdown.Toggle
            onMouseEnter={() => handleDropdownOpenAndClose(linkNode.label)}
            className={`${styles.navLink} ${styles.toggle} ${activeDropdown === linkNode.label ? styles.active : ''}`}
            as={Nav.Link}
          >
            {linkNode.label} <span className={`${activeDropdown === linkNode.label ? styles.caretUp : styles.caretDown}`} />
          </Dropdown.Toggle>
          <Dropdown.Menu
            className={styles.dropdownMenu}
            onMouseEnter={() => handleDropdownOpenAndClose(linkNode.label)}
            onMouseLeave={() => handleDropdownOpenAndClose('')}
          >
            {renderDropdownItems(linkNode.dropdown)}
          </Dropdown.Menu>
        </Dropdown>
      );
    }
    return (
      <Nav.Link
        className={classNames(styles.navLink, className)}
        href={linkNode.linkPath}
        data-testid={linkNode.label}
        onClick={() => linkNode.onClick?.()}
      >
        {linkNode.label}
      </Nav.Link>
    );
  }

  function renderProButton() {
    if (!currentUser) {
      return null;
    }

    if (currentUser.pro) {
      return null;
    }

    return (
      <div className={styles.proButton}>
        <MainButton
          onClick={() => getAmplitudeInstance().logEvent(PRO_GLOBAL_HEADER_UPGRADE_CLICKED)}
          href={checkLinkAndAppendLanguage('/pro?ref=header')}
          text={UPGRADE_TO_PRO}
        />
      </div>
    );
  }

  function renderUserInfo() {
    return (
      <Col>
        <Nav className={`${styles.vCenter}`}>
          {renderLinkNode(links.help, `${styles.light} pr-4 ml-auto`)}
          {renderProButton()}
          <NavDropdown
            alignRight
            show={activeDropdown === 'userInfo' || false}
            data-testid="navigation-navDropdown"
            className={`${styles.noCaret} ${styles.dropdown} ${styles.userInfo}`}
            onMouseEnter={() => handleDropdownOpenAndClose('userInfo')}
            onMouseLeave={() => handleDropdownOpenAndClose('')}
            title={<ProfileIcon link={user.linkPath} imagePath={user.imagePath} />}
          >
            <div>{renderDropdownItems(user.dropdown)}</div>
          </NavDropdown>
        </Nav>
      </Col>
    );
  }

  function renderSignupLogin() {
    return (
      <Col className={`text-right ${styles.entry}`}>
        <Nav className={`${styles.vCenter}`}>
          {renderLinkNode(links.help, `${styles.light} pr-4 ml-auto`)}
          <div className={styles.entry} data-testid="navigation-signupButton">
            <MainButton
              href={user.signup.linkPath}
              onClick={() => getAmplitudeInstance().logEvent(GLOBAL_NAV_SIGNUP_BUTTON_TAPPED)}
              text={user.signup.label}
              data-testid={user.signup.label}
            />
          </div>
          <div className={styles.entry} data-testid="navigation-loginButton">
            <MainButton type="outline" href={user.login.linkPath} text={user.login.label} data-testid={user.login.label} />
          </div>
        </Nav>
      </Col>
    );
  }

  function renderUserOrSignup() {
    return user.display ? renderUserInfo() : renderSignupLogin();
  }

  const { communityFeatureFlag } = featureFlags;

  return (
    <div className="xlate-none">
      <div id="g_one_tap" className={styles.gOneTap} />
      <Navbar id={styles.nav} collapseOnSelect expand="md" className={styles.nav}>
        <Navbar.Toggle aria-controls="responsive-navbar-nav" />
        <Navbar.Collapse id="responsive-navbar-nav">
          <Col>
            <Nav className="mr-auto">
              {renderLinkNode(links.explore, styles.pageLink)}
              {communityFeatureFlag && renderLinkNode(links.community, styles.pageLink)}
              {renderLinkNode(links.saved, styles.pageLink)}
              {renderLinkNode(getShopLink(ipCountryCode), styles.pageLink)}
            </Nav>
          </Col>
          <Col className={`${styles.logoContainer}`}>
            <Navbar.Brand className="mx-auto">
              <Logo proBadgeLink={brand.proBadge.linkPath} />
            </Navbar.Brand>
          </Col>
          {renderUserOrSignup()}
        </Navbar.Collapse>
      </Navbar>
    </div>
  );
}

Navigation.propTypes = {
  currentUser: object,
  featureFlags: shape({
    communityFeatureFlag: bool
  }),
  messagingChannel: object
};

Navigation.defaultProps = {
  currentUser: null,
  featureFlags: {
    communityFeatureFlag: false
  },
  messagingChannel: postal.channel('navigation_messagingChannel')
};

export default Navigation;
